"use client";

import { c as _c } from "react/compiler-runtime";
import { FaClock } from "react-icons/fa";
import { PaymentMethodPicker } from "@/components/payment-method-picker";
import { formatCurrency, formatTimeLeft } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
const URGENT_THRESHOLD_SECONDS = 60;
interface BookingFeeCheckoutProps {
  bookingFeeCents: number;
  bookingFeeCreditAmountCents?: number | null;
  refundableBeforeHours?: number | null;
  bookingFeeText?: string | null;
  /** Cancellation and no-show terms the saved card will be held to. */
  cardPolicy?: string;
  timeLeft: number;
  patientId: string | null | undefined;
  payWithSavedMethod: (paymentMethodId: string) => Promise<void>;
  onComplete: () => void;
  /** True while the server's authoritative READY terms are still in flight. */
  isAmountLoading?: boolean;
  className?: string;
}

/**
 * Booking-fee checkout body: header, countdown banner, payment picker.
 *
 * Must render inside `RainforestPayinSessionProvider`. Parents own the expiry
 * timer (via useExpiryTimer) and unmount this component when expired.
 */
const BookingFeeCheckout = t0 => {
  const $ = _c(47);
  const {
    bookingFeeCents,
    bookingFeeCreditAmountCents: t1,
    refundableBeforeHours,
    bookingFeeText,
    cardPolicy,
    timeLeft,
    patientId,
    payWithSavedMethod,
    onComplete,
    isAmountLoading: t2,
    className
  } = t0;
  const bookingFeeCreditAmountCents = t1 === undefined ? 0 : t1;
  const isAmountLoading = t2 === undefined ? false : t2;
  const showTimer = timeLeft > 0;
  const urgentTimer = timeLeft <= URGENT_THRESHOLD_SECONDS;
  let t3;
  if ($[0] !== refundableBeforeHours) {
    t3 = refundableBeforeHours != null && Number.isFinite(refundableBeforeHours);
    $[0] = refundableBeforeHours;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const showRefundCopy = t3;
  const creditAmountCents = bookingFeeCreditAmountCents ?? 0;
  let t4;
  if ($[2] !== creditAmountCents) {
    t4 = Number.isFinite(creditAmountCents) && creditAmountCents > 0;
    $[2] = creditAmountCents;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const showCreditCopy = t4;
  const fullAmountBecomesCredit = bookingFeeCents > 0 && creditAmountCents === bookingFeeCents;
  const checkoutFeeLabel = fullAmountBecomesCredit ? "deposit" : "booking fee";
  let t5;
  if ($[4] !== creditAmountCents || $[5] !== fullAmountBecomesCredit) {
    t5 = fullAmountBecomesCredit ? "This deposit becomes a credit you can use toward your appointment." : `${formatCurrency(creditAmountCents)} of this booking fee becomes a credit you can use toward your appointment.`;
    $[4] = creditAmountCents;
    $[5] = fullAmountBecomesCredit;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const creditCopy = t5;
  let t6;
  if ($[7] !== className) {
    t6 = cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className);
    $[7] = className;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h1 className="mb-2 text-h1 text-foreground">Checkout</h1>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== showTimer || $[11] !== timeLeft || $[12] !== urgentTimer) {
    t8 = showTimer && <div className={cx("flex items-center gap-2 rounded-lg px-3 py-2 text-body", urgentTimer ? "bg-status-error-bg" : "bg-status-warning-bg")}><FaClock className={cx(urgentTimer ? "text-status-error-text" : "text-status-warning-text")} /><span className={cx("font-medium", urgentTimer ? "text-status-error-text" : "text-status-warning-text")}>{formatTimeLeft(timeLeft)} remaining to complete payment</span></div>;
    $[10] = showTimer;
    $[11] = timeLeft;
    $[12] = urgentTimer;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== bookingFeeCents) {
    t9 = formatCurrency(bookingFeeCents);
    $[14] = bookingFeeCents;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = showRefundCopy && ` (Refundable if canceled before ${refundableBeforeHours}h from appointment start)`;
  let t11;
  if ($[16] !== t10) {
    t11 = <span>{t10}</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== checkoutFeeLabel || $[19] !== t11 || $[20] !== t9) {
    t12 = <p>This appointment has a {checkoutFeeLabel} of{" "}{t9} to secure your spot.{t11}</p>;
    $[18] = checkoutFeeLabel;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== bookingFeeText) {
    t13 = bookingFeeText && <p>{bookingFeeText}</p>;
    $[22] = bookingFeeText;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== creditCopy || $[25] !== showCreditCopy) {
    t14 = showCreditCopy && <p>{creditCopy}</p>;
    $[24] = creditCopy;
    $[25] = showCreditCopy;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== cardPolicy) {
    t15 = cardPolicy && <p>{cardPolicy}</p>;
    $[27] = cardPolicy;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t15) {
    t16 = <div className="mt-3 space-y-2 text-foreground-secondary">{t12}{t13}{t14}{t15}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16 || $[35] !== t8) {
    t17 = <div className="mb-6">{t7}{t8}{t16}</div>;
    $[34] = t16;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== bookingFeeCents || $[38] !== isAmountLoading || $[39] !== onComplete || $[40] !== patientId || $[41] !== payWithSavedMethod) {
    t18 = <PaymentMethodPicker patientId={patientId} amountCents={bookingFeeCents} payWithSavedMethod={payWithSavedMethod} onComplete={onComplete} isAmountLoading={isAmountLoading} />;
    $[37] = bookingFeeCents;
    $[38] = isAmountLoading;
    $[39] = onComplete;
    $[40] = patientId;
    $[41] = payWithSavedMethod;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18 || $[45] !== t6) {
    t19 = <div data-testid="booking-fee-checkout" className={t6}>{t17}{t18}</div>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t6;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};
export default BookingFeeCheckout;